import { Badge, Button, Flex, Text, Tooltip } from "@mantine/core";

import SvgCheck from "../../../../components/Icons/Check.tsx";
import SvgClose from "../../../../components/Icons/Close.tsx";
import SvgPlus from "../../../../components/Icons/Plus.tsx";
import {
  LimitRequestStatus,
  LimitRequestType,
  OfferLetterStatus,
} from "../../../../graphql/generated.ts";
import { LimitRequestDetail } from "../../../../types/limitRequest/limitRequest.ts";
import { OfferLetter } from "../../../../types/offerLetter.ts";

interface Props {
  limitRequest: LimitRequestDetail | null | undefined;
  offeringLetter: OfferLetter | null | undefined;
  canEdit: boolean;
  onAddData: () => void;
  onActivateLimit: () => void;
  onRejectLimit: () => void;
}

export const useOfferingLetterCardFooter = ({
  limitRequest,
  canEdit,
  onActivateLimit,
  onRejectLimit,
  offeringLetter,
  onAddData,
}: Props) => {
  if (!offeringLetter) {
    return (
      <Button
        variant="filledGolden"
        leftIcon={<SvgPlus />}
        w="100%"
        uppercase
        disabled={
          limitRequest?.requestStatus !== LimitRequestStatus.Ol || !canEdit
        }
        onClick={onAddData}
      >
        Add Data
      </Button>
    );
  }

  if (limitRequest?.requestStatus === LimitRequestStatus.Approved) {
    return (
      <Badge size="m" variant="active" w="100%" leftSection={<SvgCheck />}>
        Activated limit
      </Badge>
    );
  }

  if (limitRequest?.requestStatus === LimitRequestStatus.Rejected) {
    return (
      <Badge size="m" variant="updated" w="100%">
        <Flex>
          <SvgClose />
          Rejected limit
        </Flex>
      </Badge>
    );
  }

  if (
    [
      OfferLetterStatus.RejectedByCustomer,
      OfferLetterStatus.WaitingForCustomer,
    ].includes(offeringLetter.olStatus)
  ) {
    return (
      <Button
        variant="filledGolden"
        leftIcon={<SvgClose />}
        w="100%"
        uppercase
        disabled={!canEdit}
        onClick={onRejectLimit}
      >
        Reject limit
      </Button>
    );
  }

  const hasGeneralLimit =
    limitRequest?.limitRequestType === LimitRequestType.GeneralRenewal &&
    limitRequest?.company.hasGeneralLimit;
  const tooltipText = hasGeneralLimit
    ? "Another General Limit is ACTIVE or FREEZE! Make sure the General Limit is INACTIVE to proceed with renewal."
    : undefined;
  const disabled =
    !canEdit ||
    offeringLetter.olStatus !== OfferLetterStatus.Active ||
    hasGeneralLimit;
  return (
    <Tooltip
      label={<Text size="sm">{tooltipText}</Text>}
      disabled={!tooltipText}
      withinPortal
      multiline
      withArrow
      arrowSize={10}
    >
      <Flex w="100%">
        <Button
          variant="filledGolden"
          leftIcon={<SvgCheck />}
          w="100%"
          uppercase
          disabled={disabled}
          onClick={onActivateLimit}
        >
          Activate limit
        </Button>
      </Flex>
    </Tooltip>
  );
};
